/**
 * PresentationResultSlot — компактный «слот результата» в upload-area
 * PresentationControls. Отображает состояние генерации (empty/loading/done)
 * и по клику открывает CardModal с деталями + download.
 *
 * Работает по аналогии с result-slot в image-генерации — квадратная (или
 * близкая к квадрату) карточка рядом с upload-слотом.
 *
 * Состояния:
 *   - empty    → иконка 📑 + плейсхолдер «Тут появится презентация»
 *   - loading  → spinner + «Готовим... 1–2 мин»
 *   - done     → иконка 📑 + имя + бейдж режима (ред/создана). Клик → preview.
 */
import { useGenerate } from '../../../context/GenerateContext'
import DotFieldLoader from '../../../components/DotFieldLoader'

interface Props {
	/** Открыть preview (CardModal) — передаётся из GenerateWidget
	 *  через GenerateInputBar. Клик по «done» состоянию триггерит это. */
	onOpenPreview?: () => void
}

export default function PresentationResultSlot({ onOpenPreview }: Props) {
	const { isLoading, result, settings } = useGenerate()

	const mode = settings.presentation?.mode ?? 'edit'
	const doneLabel = mode === 'create' ? 'Создана' : 'Отредактирована'

	if (isLoading) {
		return (
			<div
				className="pres-result-slot pres-result-slot--loading"
				aria-label="Генерация презентации"
				aria-busy="true"
			>
				<DotFieldLoader size={56} />
				<div className="pres-result-slot__label">Готовим... 1–2 мин</div>
			</div>
		)
	}

	if (result) {
		return (
			<button
				type="button"
				className="pres-result-slot pres-result-slot--done"
				onClick={onOpenPreview}
				aria-label="Открыть предпросмотр презентации"
				title="Открыть предпросмотр презентации"
			>
				<div className="pres-result-slot__icon" aria-hidden="true">📑</div>
				<div className="pres-result-slot__label">{doneLabel}</div>
				<div className="pres-result-slot__hover" aria-hidden="true">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round" />
						<polyline points="9 21 3 21 3 15" strokeLinecap="round" strokeLinejoin="round" />
						<line x1="21" y1="3" x2="14" y2="10" strokeLinecap="round" />
						<line x1="3" y1="21" x2="10" y2="14" strokeLinecap="round" />
					</svg>
				</div>
			</button>
		)
	}

	// Empty state — плейсхолдер до первой генерации
	return (
		<div
			className="pres-result-slot pres-result-slot--empty"
			aria-label="Тут появится сгенерированная презентация"
		>
			<div className="pres-result-slot__icon" aria-hidden="true">📑</div>
			<div className="pres-result-slot__label">Тут появится презентация</div>
		</div>
	)
}
